import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "../../shared/api/client";
import type { ContractDetail, ContractSummary } from "./contract-types";

export function useContractListWorkspace(scopeKey: string) {
  const [contracts, setContracts] = useState<ContractSummary[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetailState] = useState<ContractDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const listRequest = useRef(0);
  const detailRequest = useRef(0);
  const loadedScope = useRef(scopeKey);
  const detailScope = useRef(scopeKey);
  const scope = useRef(scopeKey);
  scope.current = scopeKey;
  const visibleSelectedId = loadedScope.current === scopeKey ? selectedId : "";
  function setDetail(value: ContractDetail | null) { detailScope.current = scopeKey; setDetailState(value); }
  const load = useCallback(async () => {
    const sequence = ++listRequest.current;
    const requestedScope = scope.current;
    setLoading(true);
    setError("");
    try {
      const result = await apiRequest<ContractSummary[]>("/api/contracts", { cache: "no-store" });
      if (sequence !== listRequest.current || requestedScope !== scope.current) return;
      loadedScope.current = requestedScope;
      setContracts(result);
      setSelectedId(current => result.some(item => item.id === current) ? current : result[0]?.id || "");
    } catch (caught) {
      if (sequence === listRequest.current && requestedScope === scope.current) {
        setError(caught instanceof Error ? caught.message : "Sözleşmeler alınamadı.");
      }
    } finally {
      if (sequence === listRequest.current && requestedScope === scope.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    setContracts([]);
    setSelectedId("");
    setDetail(null);
    void load();
    return () => { listRequest.current++; detailRequest.current++; };
  }, [load, scopeKey]);
  useEffect(() => {
    const sequence = ++detailRequest.current;
    const requestedScope = scopeKey;
    setDetail(null);
    setError("");
    if (!visibleSelectedId) return;
    apiRequest<ContractDetail>(`/api/contracts/${visibleSelectedId}`, { cache: "no-store" })
      .then(result => { if (sequence === detailRequest.current && requestedScope === scope.current) setDetail(result); })
      .catch((caught: unknown) => {
        if (sequence === detailRequest.current && requestedScope === scope.current) {
          setError(caught instanceof Error ? caught.message : "Sözleşme detayı alınamadı.");
        }
      });
    return () => { detailRequest.current++; };
  }, [visibleSelectedId, scopeKey]);
  return { contracts: loadedScope.current === scopeKey ? contracts : [], selectedId: visibleSelectedId, setSelectedId, detail: detailScope.current === scopeKey && detail?.id === visibleSelectedId ? detail : null, setDetail, loading, error, load };
}
